import { z } from 'zod';

const LogicalOperatorSchema = z.enum(['AND', 'OR']);

const patientDataFields = ['birthdate', 'gender', 'chronicIllnesses'] as const;
export type PatientDataField = (typeof patientDataFields)[number];

const QuestionTargetSchema = z.object({
  source: z.literal('question'),
  questionId: z.string(),
  questionLabel: z.string().default(''),
});

const PatientDataTargetSchema = z.object({
  source: z.literal('patientData'),
  field: z.enum(patientDataFields),
  questionLabel: z.string().default(''),
});

const InitialQuestionTargetSchema = z.object({
  source: z.literal('initial-question'),
  questionId: z.string(),
  questionLabel: z.string().default(''),
});

export const TargetSchema = z.discriminatedUnion('source', [
  QuestionTargetSchema,
  PatientDataTargetSchema,
  InitialQuestionTargetSchema,
]);

const BaseConditionSchema = z.object({
  id: z.string(),
  target: TargetSchema,
});

const valueConditionSchema = BaseConditionSchema.extend({
  operator: z.enum([
    'equals',
    'not_equals',
    'contains',
    'not_contains',
    'greater_than',
    'less_than',
  ]),
  value: z.union([z.number(), z.string(), z.string().date()]),
});

const betweenConditionSchema = BaseConditionSchema.extend({
  operator: z.literal('between'),
  minValue: z.union([z.number(), z.string().date()]),
  maxValue: z.union([z.number(), z.string().date()]),
});

const emptyConditionSchema = BaseConditionSchema.extend({
  operator: z.enum(['is_empty', 'is_not_empty']),
});

export const ConditionSchema = z.discriminatedUnion('operator', [
  valueConditionSchema,
  betweenConditionSchema,
  emptyConditionSchema,
]);

export const ConditionGroupSchema = z.object({
  logicalOperator: LogicalOperatorSchema,
  conditions: z.array(ConditionSchema),
});

// Export inferred types from schemas
export type Condition = z.infer<typeof ConditionSchema>;
export type ConditionGroup = z.infer<typeof ConditionGroupSchema>;
export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'is_empty'
  | 'is_not_empty';
export type ConditionTarget = z.infer<typeof TargetSchema>;
export type QuestionTarget = z.infer<typeof QuestionTargetSchema>;
export type PatientDataTarget = z.infer<typeof PatientDataTargetSchema>;
export type InitialQuestionTarget = z.infer<typeof InitialQuestionTargetSchema>;

// Type for conditions that have a value property
export type ConditionWithValue = z.infer<typeof valueConditionSchema>;
